/**
 * The live `/trophies` container (#583/#584 N1 slice S7; design D5, D9, tests T13, T14, T23).
 *
 * **Who it reads.** `GET /api/auth/verify` first, because nothing else the browser holds names the
 * caller (the reason `TodayRoute.jsx` and `WalletRoute.jsx` give). A child reads only their own id
 * and never asks for the family list, so a sibling's private detail is never requested. A parent
 * also reads `GET /api/admin/users?includeSelf=true` (parent only) and may choose whose trophies to
 * read. The server re-checks both cases live (S6's `trophies.service.js`, S3's `streakRead.service.js`).
 *
 * **What it reads.** `GET /api/trophies/:userId` (S6): badges with their basis, versioned goals and
 * candles, the S3 streak with 28 days and freezes, credited XP with its coverage start, and an as-of.
 * Then, on that live path only, `GET /api/story-progress/:userId` (S13) for one secondary level line.
 *
 * **While the story switch is off** that read answers `{ enabled: false }`. The screen then shows
 * what is true today, from the reads that already serve it — the badge catalog, the member's earned
 * badges, their goal progress, and the S3 streak read (which gives the labelled earlier record while
 * the streak switch is off) — and says the story view is not on yet. No basis, candle or verified
 * day is invented. `lib/trophiesMapping.js` owns every sentence.
 *
 * **Recovery reads, not socket trust (D5).** A window focus re-reads quietly: the answer replaces
 * the screen only if it is still the newest read for the same member and it is readable; a transient failure
 * preserves the dated snapshot; an authorization refusal clears private data. A focus while a read for that member is still out starts nothing.
 * The household's live channel starts the SAME quiet read (#583 unit 10): a `badge.earned` for the member on
 * screen, and socket rejoins (the first join is covered by arrival). The event is only a hint: its payload is never shown, and a badge naming
 * somebody else starts nothing. A hint while a read for that member is out (its level read included) queues ONE trailing read, because
 * one completion can award several badges and the read already out may predate the later ones.
 * A read that exceeds the shared timeout releases that hint and loses authority to publish a late answer.
 *
 * **Whose clock, and whose name.** `GET /api/tenants/current` (any member) gives the household zone
 * and currency, used when the streak read carries no policy; a failed read leaves instants labelled
 * UTC and the room on screen. The signed-in member is named by the server's one rule
 * (`profile.service` `shownName`: first name, else display name), from `GET /api/auth/profile`, the
 * caller's own row. A username is never a fallback (#583 D-B: a Lantern
 * sign-up parent has only a display name, and their email username was the greeting).
 *
 * **A family turn (#1003) reads one member, never the account.** During a child's turn on a parent's device
 * verify's `user` is the PARENT, so the account path above asked for the parent's trophies and was refused (#905
 * D5). The member and the turn's generation come from the actor context's `familyTurn` (`lib/familyTurnSubject.js`),
 * and the screen reads only `GET /api/trophies/:member` and its level line `GET /api/story-progress/:member`, each
 * naming the generation, which the server requires. No verify, profile, household, member list or legacy read: the
 * turn keeps them closed, so there is no chooser, the name is the turn's, and instants are labelled UTC. With story
 * credits off it says the trophies are not available during the turn instead of falling back to the legacy reads.
 * A refusal is said as the turn's sentence (`turnReadMessage`), with no retry, and a turn code reports the turn ended. A
 * plain 401 or 403 is a refusal too (`turnReadRefusal`): a service can refuse after the gate accepts. A reload
 * mounts this screen before the session answers, so nothing is read until it says whether a turn is on.
 */
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import LanternLoader from '../components/LanternLoader.jsx';
import { ApiError, api } from '../api/client.js';
import { fetchTenant } from '../api/session.js';
import { reportTurnRefusal, turnReadMessage, turnReadRefusal } from '../lib/familyInteraction.js';
import { turnSubject } from '../lib/familyTurnSubject.js';
import { STORY_LEVEL_UNAVAILABLE, screenZone, toLegacyTrophies, toLiveTrophies, toStoryLevelView } from '../lib/trophiesMapping.js';
import { useOptionalActor } from '../state/ActorContext.jsx';
import { READ_TIMEOUT_MS, useHouseholdHint } from '../state/HouseholdRealtime.jsx';

const Trophies = lazy(() => import('../pages/Trophies.jsx'));

export const LOAD_FAILED = 'We couldn’t load your trophy room just now — give it another moment.';
export const UNREADABLE = 'Your trophies came back in a shape we did not expect. Trying again may not help — please tell us if it keeps happening.';
export const FORBIDDEN = 'You are not allowed to see these trophies.';
export const SIGNED_OUT = 'Your sign-in has expired — please sign in again.';
export const NOT_FOUND = 'That family member was not found.';

function messageFor(error) {
  if (error instanceof ApiError) {
    if (error.status === 401) return SIGNED_OUT;
    if (error.status === 403) return error.body?.error === 'You are no longer an active member of this family'
      ? 'You are no longer an active member of this family.' : FORBIDDEN;
    if (error.status === 404) return error.body?.error === 'No such family member'
      ? NOT_FOUND : 'The trophy service is not available on this server right now.';
  }
  return LOAD_FAILED;
}

const realText = (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
/** A name that may be shown: real text that is not an email address. */
const shownText = (v) => {
  const t = realText(v);
  return t !== null && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t) ? t : null;
};
const memberName = (member) => realText(member.firstName) ?? realText(member.displayName);

/** Compose from the raw name columns: Family Studio's display-first mapping serves other callers. */
function trophiesMembers(list) {
  const users = Array.isArray(list?.users) ? list.users : [];
  return users.filter((m) => m && typeof m === 'object' && m.isActive !== false && Number.isInteger(m.id))
    .map((m) => ({ id: m.id, name: memberName(m) }));
}

/** A hint that plainly names another member: an integer id, or its digits. Anything else names nobody, so it still reads. */
function namesSomeoneElse(payload, memberId) {
  const raw = payload && typeof payload === 'object' ? payload.userId : undefined;
  const id = typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw) : raw;
  return Number.isSafeInteger(id) && id !== memberId;
}

/** #1003: what a family turn's read answers while story credits are off. The legacy reads stay closed to a turn. */
const TURN_STORY_OFF = Object.freeze({ turnStoryOff: true });

/**
 * One member's trophies: the story read, or — while it is off — the reads that serve today. `generation` (#1003): a
 * family turn's, named on both reads; with it, story credits off answer `TURN_STORY_OFF` and nothing more is read.
 */
async function readTrophies(userId, household, signal, onRoomReady, generation = null) {
  const options = generation ? { signal, familyGeneration: generation } : { signal };
  const trophies = await api.get(`/api/trophies/${userId}`, options);
  if (generation && trophies && typeof trophies === 'object' && trophies.enabled === false) return TURN_STORY_OFF;
  if (trophies && typeof trophies === 'object' && trophies.enabled === false) {
    const [catalog, earned, progress, streak] = await Promise.all([
      api.get('/api/achievements', { signal }),
      api.get(`/api/achievements/user?userId=${userId}`, { signal }),
      api.get(`/api/goals/progress?userId=${userId}`, { signal }),
      api.get(`/api/streaks/${userId}`, { signal }).catch((error) => {
        if (error instanceof ApiError && [401, 403, 404].includes(error.status)) throw error;
        return null;
      }),
    ]);
    return toLegacyTrophies({ catalog, earned, progress, streak, household });
  }
  // S13 owns the XP coverage: S6 only knows the rollout start, which can be later than credited work.
  const view = toLiveTrophies(trophies, household, { includeXp: false });
  if (!view) return view;
  // Publish the room before its secondary read settles, including during a quiet focus refresh.
  onRoomReady?.(view);
  // S13's level line: read only on this live path, so the off path above makes exactly the reads it did before.
  // A failed level read never takes the room down; it says the level is not available. A refusal is still a
  // refusal (the streak read's rule above), and a 404 is a server from before S13, so it stays "not available".
  const progress = await api.get(`/api/story-progress/${userId}`, options).catch((error) => {
    if (signal?.aborted) throw error;
    if (error instanceof ApiError && [401, 403].includes(error.status)) throw error;
    return null;
  });
  // Two reads, two instants: one screen never states two XP totals, so a disagreement is "not available".
  const credited = trophies.xp?.credited;
  const level = Number.isSafeInteger(credited) && progress?.enabled === true && progress.xp !== credited
    ? null : toStoryLevelView(progress, screenZone(trophies.streak, household));
  return { ...view, level: level === null ? STORY_LEVEL_UNAVAILABLE : level.sentence };
}

/** The screen while nothing may be read yet: the session's first answer, which says whether a turn is on. */
function WaitingTrophies() {
  return (
    <Suspense fallback={<LanternLoader label="Waiting to learn which badges and goals you have earned." />}>
      <Trophies showSnapshotNotice={false} loading />
    </Suspense>
  );
}

/** #1003: the turn's member as this screen reads trophies for it, with the turn's generation. */
const trophiesMember = (member, familyTurn) => ({ id: member.userId, name: shownText(member.firstName), generation: familyTurn.generation });

/** What a turn holding no live member says: it has ended, its session has, or its state could not be read. */
const TURN_STATE_STATUS = Object.freeze({ locked: 'locked', revoked: 'revoked' });

/**
 * #1003: one member's trophy room during a family turn. Arrival and window focus read; focus while a read is out
 * starts nothing, and a quiet read that fails keeps what is on screen. A turn refusal stops every later read: waiting
 * does not open it. Every answer is drawn only for the turn (member and generation) it was read for.
 */
function TurnTrophies({ familyTurn }) {
  const subject = turnSubject(familyTurn, trophiesMember);
  const key = subject.key;
  const memberRef = useRef(subject.member);
  memberRef.current = subject.member;
  const [stored, setPage] = useState({ key: null, status: 'loading', view: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const member = memberRef.current;
    if (!member) return undefined;
    let gone = false;
    let stopped = false;
    let flight = null;
    let seq = 0;
    const read = (quiet) => {
      if (gone || stopped || (quiet && flight)) return;
      const controller = new AbortController();
      const n = (seq += 1);
      flight = controller;
      if (!quiet) setPage({ key, status: 'loading', view: null, error: null });
      const timer = setTimeout(() => controller.abort(), READ_TIMEOUT_MS);
      const current = () => !gone && n === seq;
      readTrophies(member.id, null, controller.signal, (view) => {
        if (current()) setPage({ key, status: 'ready', view, error: null });
      }, member.generation)
        .then((view) => {
          if (!current()) return;
          if (view === TURN_STORY_OFF) setPage({ key, status: 'off', view: null, error: null });
          else if (view) setPage({ key, status: 'ready', view, error: null });
          else if (!quiet) setPage({ key, status: 'error', view: null, error: 'unreadable' });
        })
        .catch((error) => {
          if (!current()) return;
          // A plain 401 or 403 is a refusal too (`turnReadRefusal`): the service can refuse after the gate accepts.
          if (turnReadRefusal(error) !== 'failed') {
            stopped = true;
            reportTurnRefusal(member.generation, error);
            setPage({ key, status: 'error', view: null, error });
          } else if (!quiet) {
            setPage({ key, status: 'error', view: null, error });
          }
        })
        .finally(() => {
          clearTimeout(timer);
          if (flight === controller) flight = null;
        });
    };
    read(false);
    const onFocus = () => read(true);
    window.addEventListener('focus', onFocus);
    return () => {
      gone = true;
      flight?.abort();
      window.removeEventListener('focus', onFocus);
    };
  }, [key, reloadKey]);

  const page = stored.key === key ? stored : { status: 'loading', view: null, error: null };
  const view = page.status === 'ready' ? page.view : null;
  let error = null;
  let retry;
  if (!subject.member) {
    error = turnReadMessage('trophies', TURN_STATE_STATUS[familyTurn.state] ?? 'failed');
  } else if (page.status === 'off') {
    error = turnReadMessage('trophies', 'forbidden');
  } else if (page.status === 'error') {
    const status = page.error === 'unreadable' ? null : turnReadRefusal(page.error);
    error = status === null ? UNREADABLE : turnReadMessage('trophies', status);
    // Only a read that did not answer may be asked again; a refusal does not open by asking.
    if (status === null || status === 'failed') retry = () => setReloadKey((n) => n + 1);
  }
  const name = subject.member?.name ?? '';

  return (
    <Suspense fallback={<LanternLoader label="Waiting to learn which badges and goals you have earned." />}>
      <Trophies
        showSnapshotNotice={false}
        otherMember={false}
        kidName={name}
        streak={view ? view.streak : null}
        latestBadge={view?.latestBadge}
        badges={view ? view.badges : []}
        goals={view ? view.goals : []}
        streakHistory={view ? view.streakHistory : null}
        level={view ? view.level ?? null : null}
        notes={view ? view.notes : []}
        loading={!error && page.status === 'loading'}
        error={error}
        onRetry={retry}
      />
    </Suspense>
  );
}

/**
 * The live `/trophies` route: a family turn's member (#1003), or the signed-in account. Until the session's first
 * answer says which, nothing is read, so a reload during a turn never asks as the parent.
 */
export default function TrophiesRoute() {
  const actor = useOptionalActor();
  if (actor?.familyTurn) return <TurnTrophies familyTurn={actor.familyTurn} />;
  if (actor?.status === 'loading') return <WaitingTrophies />;
  return <AccountTrophies />;
}

function AccountTrophies() {
  const [who, setWho] = useState({ status: 'loading', me: null, myName: null, members: [], error: null });
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState({ status: 'loading', view: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [verifyKey, setVerifyKey] = useState(0);
  const readSeq = useRef(0);
  // The household's zone and currency, or null. Read once with identity; every read after uses it.
  const household = useRef(null);
  // The member whose read is out right now, so a focus does not start a second one (review L7).
  const inFlight = useRef(null);
  // The member whose read has not SETTLED, level read included. Publishing the room frees a focus retry
  // (inFlight), but a hint still waits for this, so a burst of badges queues one trailing read (review M-A).
  const readBusy = useRef(null);
  // A live-channel hint that arrived while that read was out, and the quiet read that answers it.
  const hintPending = useRef(false);
  const recover = useRef(null);
  const alive = useRef(true);
  const readTimers = useRef(new Set());
  useEffect(() => {
    alive.current = true;
    const timers = readTimers.current;
    return () => {
      alive.current = false;
      readSeq.current += 1;
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const flushHint = useCallback(() => {
    if (!hintPending.current || !alive.current) return;
    hintPending.current = false;
    recover.current?.();
  }, []);

  // A hung room or secondary level read cannot hold a hint indefinitely. Only its newest read
  // may release the busy marks; invalidating its sequence also drops clients' late answers.
  const boundRead = useCallback((seq, controller) => {
    const timer = setTimeout(() => {
      readTimers.current.delete(timer);
      if (!alive.current || seq !== readSeq.current || controller.signal.aborted) return;
      readSeq.current += 1;
      controller.abort();
      inFlight.current = null;
      readBusy.current = null;
      flushHint();
    }, READ_TIMEOUT_MS);
    readTimers.current.add(timer);
    return () => { clearTimeout(timer); readTimers.current.delete(timer); };
  }, [flushHint]);

  // A cookie or live role may have changed since the parent chooser was loaded.
  // A refusal invalidates that authority as well as the private room; retry verifies again.
  const dropAuthority = useCallback((error) => {
    if (error instanceof ApiError && [401, 403].includes(error.status)) {
      readSeq.current += 1;
      setSelected(null);
      setWho({ status: 'error', me: null, myName: null, members: [], error });
    }
  }, []);

  // Who am I, and — for a parent only — who may I choose.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    // Neither of these may fail the screen: a missing zone is labelled UTC, a missing name falls back.
    const tenant = fetchTenant().catch(() => null);
    const profile = api.get('/api/auth/profile', { signal }).catch(() => null);
    api.get('/api/auth/verify', { signal })
      .then(async (me) => {
        const id = me?.user?.id ?? me?.user?.userId;
        if (!Number.isInteger(id)) throw new Error('verify named no member');
        const parent = me.user.role === 'admin';
        // A failed family list leaves the parent reading their own trophies, never a guessed child.
        const [members, t, mine] = await Promise.all([
          parent
            ? api.get('/api/admin/users?includeSelf=true', { signal }).catch(() => null)
              .then(trophiesMembers)
            : [],
          tenant,
          profile,
        ]);
        if (signal.aborted) return;
        household.current = t && typeof t === 'object' ? { timezone: t.timezone, currency: t.currency } : null;
        const myName = mine && mine.id === id ? shownText(memberName(mine)) : null;
        setWho({ status: 'ready', me: me.user, myName, members, error: null });
        // The chooser can only show a member it lists, so the screen reads one it lists (review L5).
        setSelected(members.length > 0 && !members.some((m) => m.id === id) ? members[0].id : id);
      })
      .catch((error) => {
        if (signal.aborted) return;
        setWho({ status: 'error', me: null, myName: null, members: [], error });
        setPage({ status: 'error', view: null, error: error instanceof ApiError ? error : 'unreadable' });
      });
    return () => controller.abort();
  }, [verifyKey]);

  // The chosen member's trophies. A new choice or a retry shows loading; nothing old stays under a new name.
  useEffect(() => {
    if (selected === null) return undefined;
    const controller = new AbortController();
    const seq = (readSeq.current += 1);
    setPage({ status: 'loading', view: null, error: null });
    hintPending.current = false; // This read is newer than any hint for the member it replaces.
    inFlight.current = selected;
    readBusy.current = selected;
    const clearTimer = boundRead(seq, controller);
    readTrophies(selected, household.current, controller.signal, (view) => {
      if (controller.signal.aborted || seq !== readSeq.current) return;
      setPage({ status: 'ready', view, error: null });
      inFlight.current = null; // A hung level must not block a focus retry.
    })
      .then((view) => {
        if (seq !== readSeq.current) return;
        setPage(view ? { status: 'ready', view, error: null } : { status: 'error', view: null, error: 'unreadable' });
      })
      .catch((error) => {
        if (controller.signal.aborted || seq !== readSeq.current) return;
        setPage({ status: 'error', view: null, error });
        dropAuthority(error);
      })
      .finally(() => {
        clearTimer();
        if (seq !== readSeq.current) return;
        inFlight.current = null;
        readBusy.current = null;
        if (!controller.signal.aborted) flushHint();
      });
    return () => {
      clearTimer();
      controller.abort();
      if (inFlight.current === selected) inFlight.current = null;
      if (readBusy.current === selected) readBusy.current = null;
    };
  }, [selected, reloadKey, dropAuthority, flushHint, boundRead]);

  // Focus, or a live-channel hint: a quiet re-read. Only the newest readable answer for the same member may land.
  useEffect(() => {
    if (selected === null) return undefined;
    const running = new Map();
    const quietRead = () => {
      if (inFlight.current === selected) return;
      const seq = (readSeq.current += 1);
      inFlight.current = selected;
      readBusy.current = selected;
      const controller = new AbortController();
      const clearTimer = boundRead(seq, controller);
      running.set(controller, clearTimer);
      readTrophies(selected, household.current, controller.signal, (view) => {
        if (seq !== readSeq.current) return;
        setPage({ status: 'ready', view, error: null });
        inFlight.current = null;
      })
        .then((view) => { if (view && seq === readSeq.current) setPage({ status: 'ready', view, error: null }); })
        .catch((error) => {
          if (seq === readSeq.current && error instanceof ApiError && [401, 403, 404].includes(error.status)) {
            setPage({ status: 'error', view: null, error });
            dropAuthority(error);
          }
        })
        .finally(() => {
          clearTimer();
          running.delete(controller);
          if (seq !== readSeq.current) return;
          inFlight.current = null;
          readBusy.current = null;
          flushHint();
        });
    };
    recover.current = quietRead;
    window.addEventListener('focus', quietRead);
    return () => {
      readSeq.current += 1;
      for (const [controller, clearTimer] of running) { clearTimer(); controller.abort(); }
      running.clear();
      window.removeEventListener('focus', quietRead);
      if (recover.current === quietRead) recover.current = null;
    };
  }, [selected, dropAuthority, flushHint, boundRead]);

  // Arrival covers the first join; badges and rejoins recover quietly (D5: a hint, not data).
  useHouseholdHint('trophies', (cause, payload) => {
    if (selected === null || (cause === 'event' && namesSomeoneElse(payload, selected))) return;
    if (readBusy.current === selected) { hintPending.current = true; return; }
    recover.current?.();
  }, { skipInitialJoin: true });

  // A failed identity read retries identity; otherwise the SAME member is read again (a parent's choice stays).
  const whoFailed = who.status === 'error';
  const retry = useCallback(() => (whoFailed ? setVerifyKey : setReloadKey)((n) => n + 1), [whoFailed]);

  const view = page.status === 'ready' ? page.view : null;
  const error = page.status === 'error'
    ? (page.error === 'unreadable' ? UNREADABLE : messageFor(page.error))
    : null;
  const chosen = who.members.find((m) => m.id === selected);
  const myId = who.me ? (who.me.id ?? who.me.userId) : null;
  // Both sources follow the first-name, then display-name rule; greetings omit a nameless member.
  const name = (selected === myId
    ? who.myName ?? shownText(chosen?.name)
    : shownText(chosen?.name)) ?? '';

  const otherMember = selected !== null && myId !== null && selected !== myId;
  const whose = name ? `${name}’s` : 'Their';
  const latestBadge = view?.latestBadge;

  const toolbar = who.members.length > 1 ? (
    <label className="trf-picker">
      Whose trophies
      {' '}
      <select value={selected ?? ''} onChange={(event) => setSelected(Number(event.target.value))}>
        {who.members.map((m) => <option key={m.id} value={m.id}>{shownText(m.name) ?? `Member ${m.id}`}</option>)}
      </select>
    </label>
  ) : null;

  return (
    <Suspense fallback={<LanternLoader label="Waiting to learn which badges and goals you have earned." />}>
      <Trophies
        showSnapshotNotice={false}
        otherMember={otherMember}
        kidName={name}
        streak={view ? view.streak : null}
        latestBadge={latestBadge && otherMember ? { ...latestBadge, title: latestBadge.title.replace(/^You earned /, () => `${name || 'They'} earned `) } : latestBadge}
        badges={view ? view.badges : []}
        goals={view ? view.goals : []}
        streakHistory={view ? view.streakHistory : null}
        level={view ? view.level ?? null : null}
        notes={view ? view.notes : []}
        wallHeading={otherMember ? `${whose} trophy wall` : undefined}
        goalsHeading={otherMember ? `${whose} goal candles` : undefined}
        goalsSub={otherMember ? 'Each candle shows progress toward its goal.' : undefined}
        streakHeading={otherMember ? `${whose} streak story` : undefined}
        toolbar={toolbar}
        loading={page.status === 'loading'}
        error={error}
        onRetry={retry}
      />
    </Suspense>
  );
}
